import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { AdminScript } from "@/hooks/useAdminContent";

interface ContentDetailModalProps {
  script: AdminScript | null;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-[#6FEDD6]',
  hidden: 'bg-gray-200',
  flagged: 'bg-[#FF6B9D]',
};

export function ContentDetailModal({ script, onClose }: ContentDetailModalProps) {
  const { t, i18n } = useTranslation();

  if (!script) return null;

  const getSceneTypeLabel = (sceneType: string) => {
    const labels: Record<string, string> = {
      recording: t('admin.content.sceneTypes.recording'),
      speech: t('admin.content.sceneTypes.speech'),
      meeting: t('admin.content.sceneTypes.meeting'),
      podcast: t('admin.content.sceneTypes.podcast'),
    };
    return labels[sceneType] || sceneType;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: t('admin.content.status.active'),
      hidden: t('admin.content.status.hidden'),
      flagged: t('admin.content.status.flagged'),
    };
    return labels[status] || status;
  };

  const statusColor = STATUS_COLORS[script.status] || STATUS_COLORS.active;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-testid="modal-backdrop"
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border-2 border-black bg-white shadow-[8px_8px_0_#1A1A1A]">
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-black p-6">
          <div>
            <h2 className="text-2xl font-black tracking-tight">{script.title}</h2>
            <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
              <span>{script.user?.name || script.user?.email || t('admin.content.unknownUser')}</span>
              <span>•</span>
              <span>{getSceneTypeLabel(script.scene_type)}</span>
              <span>•</span>
              <span>{t('admin.content.modal.wordCount', { count: script.word_count })}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColor}`}>
                {getStatusLabel(script.status)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border-2 border-black p-2 bg-white hover:bg-gray-100 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 max-h-[60vh]">
          <div className="prose prose-lg max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed text-[#1A1A1A] bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              {script.content || t('admin.content.modal.noContent')}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t-2 border-black p-6 bg-gray-50">
          <div className="text-sm text-gray-500">
            <span>{t('admin.content.modal.createdAt', { date: new Date(script.created_at).toLocaleString(i18n.language) })}</span>
            {script.updated_at && (
              <span className="ml-4">{t('admin.content.modal.updatedAt', { date: new Date(script.updated_at).toLocaleString(i18n.language) })}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border-2 border-black bg-white px-6 py-2 font-black shadow-[4px_4px_0_#1A1A1A] hover:translate-x-0.5 hover:translate-y-0.5 active:translate-x-1 active:translate-y-1 active:shadow-none transition-all whitespace-nowrap"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

